import React, { useState } from 'react';
import { FaBalanceScale, FaCalendarAlt, FaUser, FaExclamationTriangle, FaFileAlt, FaClipboardCheck, FaShieldAlt, FaCheckCircle } from 'react-icons/fa';

const PatientRightsForm = ({ setStep, onSubmit }) => {
  const [formData, setFormData] = useState({
    dateTimeDiscovered: '',
    dateTimeOccurred: '',
    discoveredBy: '',
    patientName: '',
    patientId: '',
    violationType: '',
    requestType: '',
    requestDate: '',
    requestDescription: '',
    responseDate: '',
    responseProvided: false,
    reasonForDenial: '',
    incidentDescription: '',
    immediateActions: '',
    patientNotified: false,
    correctiveActions: '',
    preventiveMeasures: '',
    reportedBy: '',
    reportedByTitle: '',
    reportedByEmail: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        type: 'hipaa',
        subtype: 'patientRights',
        data: formData,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-violet-100">
            <FaBalanceScale className="size-8 text-violet-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Patient Rights Violation Report
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report violation of patient access, amendment, or restriction requests
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={handleSubmit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Incident Details */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Incident Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When the violation was discovered
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dateTimeDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Date/Time Discovered <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateTimeDiscovered"
                        name="dateTimeDiscovered"
                        type="datetime-local"
                        value={formData.dateTimeDiscovered}
                        onChange={handleInputChange}
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.dateTimeDiscovered ? 'outline-red-500' : ''}`}
                      />
                      {errors.dateTimeDiscovered && <p className="mt-1 text-sm text-red-600">{errors.dateTimeDiscovered}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="dateTimeOccurred" className="block text-sm/6 font-medium text-gray-900">
                      Date/Time Violation Occurred (if different)
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateTimeOccurred"
                        name="dateTimeOccurred"
                        type="datetime-local"
                        value={formData.dateTimeOccurred}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="discoveredBy" className="block text-sm/6 font-medium text-gray-900">
                      Discovered By <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="discoveredBy"
                        name="discoveredBy"
                        type="text"
                        value={formData.discoveredBy}
                        onChange={handleInputChange}
                        placeholder="Name and title of person who discovered the violation"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.discoveredBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.discoveredBy && <p className="mt-1 text-sm text-red-600">{errors.discoveredBy}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Patient Information */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Patient Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Information about the affected patient
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="patientName" className="block text-sm/6 font-medium text-gray-900">
                      Patient Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="patientName"
                        name="patientName"
                        type="text"
                        value={formData.patientName}
                        onChange={handleInputChange}
                        placeholder="Full name of affected patient"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.patientName ? 'outline-red-500' : ''}`}
                      />
                      {errors.patientName && <p className="mt-1 text-sm text-red-600">{errors.patientName}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="patientId" className="block text-sm/6 font-medium text-gray-900">
                      Patient ID/Medical Record Number
                    </label>
                    <div className="mt-2">
                      <input
                        id="patientId"
                        name="patientId"
                        type="text"
                        value={formData.patientId}
                        onChange={handleInputChange}
                        placeholder="Patient identifier"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Violation Details */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaExclamationTriangle className="size-5 text-gray-400" />
                  Violation Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Type of rights violation and patient request
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="violationType" className="block text-sm/6 font-medium text-gray-900">
                      Type of Rights Violation <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <select
                        id="violationType"
                        name="violationType"
                        value={formData.violationType}
                        onChange={handleInputChange}
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.violationType ? 'outline-red-500' : ''}`}
                      >
                        <option value="">Select violation type</option>
                        <option value="access_denied">Access to Records Denied</option>
                        <option value="access_delayed">Access to Records Delayed</option>
                        <option value="amendment_denied">Amendment Request Denied Improperly</option>
                        <option value="amendment_delayed">Amendment Request Delayed</option>
                        <option value="restriction_denied">Restriction Request Denied Improperly</option>
                        <option value="restriction_ignored">Restriction Request Ignored</option>
                        <option value="accounting_denied">Accounting of Disclosures Denied</option>
                        <option value="accounting_incomplete">Accounting of Disclosures Incomplete</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.violationType && <p className="mt-1 text-sm text-red-600">{errors.violationType}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="requestType" className="block text-sm/6 font-medium text-gray-900">
                      Patient's Original Request <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <select
                        id="requestType"
                        name="requestType"
                        value={formData.requestType}
                        onChange={handleInputChange}
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.requestType ? 'outline-red-500' : ''}`}
                      >
                        <option value="">Select request type</option>
                        <option value="access_records">Access to Medical Records</option>
                        <option value="amend_records">Amendment to Medical Records</option>
                        <option value="restrict_disclosure">Restriction on Disclosure</option>
                        <option value="accounting_disclosures">Accounting of Disclosures</option>
                        <option value="alternative_communication">Alternative Communication Method</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.requestType && <p className="mt-1 text-sm text-red-600">{errors.requestType}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="requestDate" className="block text-sm/6 font-medium text-gray-900">
                      Date of Patient's Request
                    </label>
                    <div className="mt-2">
                      <input
                        id="requestDate"
                        name="requestDate"
                        type="date"
                        value={formData.requestDate}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="requestDescription" className="block text-sm/6 font-medium text-gray-900">
                      Description of Patient's Request <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="requestDescription"
                        name="requestDescription"
                        rows={3}
                        value={formData.requestDescription}
                        onChange={handleInputChange}
                        placeholder="Describe what the patient requested..."
                        required
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.requestDescription ? 'outline-red-500' : ''}`}
                      />
                      {errors.requestDescription && <p className="mt-1 text-sm text-red-600">{errors.requestDescription}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Our Response */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaClipboardCheck className="size-5 text-gray-400" />
                  Our Response
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  How the practice responded to the patient request
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="responseDate" className="block text-sm/6 font-medium text-gray-900">
                      Date of Our Response
                    </label>
                    <div className="mt-2">
                      <input
                        id="responseDate"
                        name="responseDate"
                        type="date"
                        value={formData.responseDate}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="responseProvided"
                          checked={formData.responseProvided}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Response was provided to patient</label>
                  </div>

                  <div>
                    <label htmlFor="reasonForDenial" className="block text-sm/6 font-medium text-gray-900">
                      Reason for Denial/Delay (if applicable)
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="reasonForDenial"
                        name="reasonForDenial"
                        rows={3}
                        value={formData.reasonForDenial}
                        onChange={handleInputChange}
                        placeholder="If request was denied or delayed, explain the reason..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Incident Description */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaFileAlt className="size-5 text-gray-400" />
                  Incident Description
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Detailed description of the violation
                </p>

                <div className="mt-10">
                  <label htmlFor="incidentDescription" className="block text-sm/6 font-medium text-gray-900">
                    Detailed Description of Violation <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2">
                    <textarea
                      id="incidentDescription"
                      name="incidentDescription"
                      rows={4}
                      value={formData.incidentDescription}
                      onChange={handleInputChange}
                      placeholder="Provide a detailed description of how patient rights were violated..."
                      required
                      className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.incidentDescription ? 'outline-red-500' : ''}`}
                    />
                    {errors.incidentDescription && <p className="mt-1 text-sm text-red-600">{errors.incidentDescription}</p>}
                  </div>
                </div>
              </div>

              {/* Response Actions */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaShieldAlt className="size-5 text-gray-400" />
                  Response Actions
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Actions taken in response to the violation
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="immediateActions" className="block text-sm/6 font-medium text-gray-900">
                      Immediate Actions Taken <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="immediateActions"
                        name="immediateActions"
                        rows={3}
                        value={formData.immediateActions}
                        onChange={handleInputChange}
                        placeholder="What immediate actions were taken when the violation was discovered?"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.immediateActions ? 'outline-red-500' : ''}`}
                      />
                      {errors.immediateActions && <p className="mt-1 text-sm text-red-600">{errors.immediateActions}</p>}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="patientNotified"
                          checked={formData.patientNotified}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Patient has been notified of the violation</label>
                  </div>

                  <div>
                    <label htmlFor="correctiveActions" className="block text-sm/6 font-medium text-gray-900">
                      Corrective Actions Taken
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="correctiveActions"
                        name="correctiveActions"
                        rows={3}
                        value={formData.correctiveActions}
                        onChange={handleInputChange}
                        placeholder="What corrective actions have been or will be taken?"
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="preventiveMeasures" className="block text-sm/6 font-medium text-gray-900">
                      Preventive Measures <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="preventiveMeasures"
                        name="preventiveMeasures"
                        rows={3}
                        value={formData.preventiveMeasures}
                        onChange={handleInputChange}
                        placeholder="What measures will be implemented to prevent similar violations?"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.preventiveMeasures ? 'outline-red-500' : ''}`}
                      />
                      {errors.preventiveMeasures && <p className="mt-1 text-sm text-red-600">{errors.preventiveMeasures}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Reporter Information */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Reporter Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Your contact information
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="reportedBy" className="block text-sm/6 font-medium text-gray-900">
                      Reported By <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedBy"
                        name="reportedBy"
                        type="text"
                        value={formData.reportedBy}
                        onChange={handleInputChange}
                        placeholder="Full name"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedBy && <p className="mt-1 text-sm text-red-600">{errors.reportedBy}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reportedByTitle" className="block text-sm/6 font-medium text-gray-900">
                      Title/Position <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByTitle"
                        name="reportedByTitle"
                        type="text"
                        value={formData.reportedByTitle}
                        onChange={handleInputChange}
                        placeholder="Job title or position"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedByTitle ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedByTitle && <p className="mt-1 text-sm text-red-600">{errors.reportedByTitle}</p>}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="reportedByEmail" className="block text-sm/6 font-medium text-gray-900">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByEmail"
                        name="reportedByEmail"
                        type="email"
                        value={formData.reportedByEmail}
                        onChange={handleInputChange}
                        placeholder="email@example.com"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedByEmail ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedByEmail && <p className="mt-1 text-sm text-red-600">{errors.reportedByEmail}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-x-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Report</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PatientRightsForm;
